<template>
    <div ref="listElement" class="list" :class="{ loading: props.isLoading }" @scroll="loadMoreDocumentsIfNeeded">
        <template v-if="props.documents.length > 0">
            <div v-for="document in visibleDocuments" :key="document.documentShortCode">
                <document-item
                    :is-data="false"
                    :pdf="document"
                    :list-layout="props.listLayout"
                    :is-unread="props.isDocumentUnread?.(document) ?? false"
                    :document-route-name="props.documentRouteName"
                    :hide-description="props.hideDescription"
                    :open-mode="props.openMode"
                    @open-document="emit('openDocument', document)"
                />
                <div class="separator" />
            </div>
        </template>
        <div v-else class="empty-search" :class="{ loading: props.isLoading }">
            {{ props.emptyMessage }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import DocumentItem from './DocumentItem.vue';

const listElement = ref<HTMLElement | null>(null);

defineExpose({
    listElement,
});

const props = withDefaults(defineProps<{
    documents: Array<IDocumentSimple>;
    listLayout: string;
    isLoading?: boolean;
    documentRouteName?: string;
    emptyMessage?: string;
    hideDescription?: boolean;
    openMode?: 'route' | 'emit';
    initialVisibleCount?: number;
    visibleCountIncrement?: number;
    isDocumentUnread?:(document: IDocumentSimple) => boolean;
}>(), {
    isLoading: false,
    documentRouteName: undefined,
    emptyMessage: 'No results found.',
    hideDescription: false,
    openMode: 'route',
    initialVisibleCount: undefined,
    visibleCountIncrement: 10,
    isDocumentUnread: undefined,
});

const emit = defineEmits<{
    openDocument: [IDocumentSimple];
}>();

const visibleDocumentCount = ref<number>(props.initialVisibleCount ?? props.documents.length);
const visibleDocuments = computed<Array<IDocumentSimple>>(() => props.documents.slice(0, visibleDocumentCount.value));

function loadMoreDocumentsIfNeeded (): void {
    if (!props.initialVisibleCount) return;
    if (!listElement.value) return;
    if (visibleDocumentCount.value >= props.documents.length) return;

    const scrollThreshold = 80;
    const scrollPosition = listElement.value.scrollTop + listElement.value.clientHeight;
    if (scrollPosition < listElement.value.scrollHeight - scrollThreshold) return;

    visibleDocumentCount.value = Math.min(
        visibleDocumentCount.value + props.visibleCountIncrement,
        props.documents.length,
    );
}

watch(() => props.documents, () => {
    visibleDocumentCount.value = props.initialVisibleCount ?? props.documents.length;
});
</script>

<style lang="scss" scoped>
.list {
    display: grid;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    grid-auto-rows: max-content;
    gap: var(--smallSpacing);
    padding: var(--defaultSpacing);
    border-radius: var(--borderRadiusOuter);

    @include responsive(sm) {
        max-height: calc(100vh - 200px);
        overflow-y: scroll;
    }

    &.loading {
        filter: blur(5px);
    }

    .separator {
        background: var(--neutral300);
        height: 1px;
        width: 90%;
        margin: var(--smallSpacing) auto 0;

        @include responsive(sm) {
            display: none;
        }
    }
}

.empty-search {
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: var(--boldFontWeight);
    color: var(--primaryFont);
    margin-top: 5rem;

    &.loading {
        filter: blur(5px);
    }
}
</style>
